import React from 'react';
import NextHead from 'next/head';
import { useRouter } from 'next/router';
import { getEnvLink } from 'helpers/routes';
import { getCanonicalUrl } from 'helpers/canonical';
import type { ImagePreload } from 'helpers/profileAvatar';
import { isProduction } from 'helpers/env';
import type { ArticleOpenGraph } from 'helpers/articleSeo';

const DEFAULT_IMAGE = '/homepage.jpg';

type VideoMetadata = {
  url: string;
  type?: string;
  duration?: number;
  width?: number;
  height?: number;
};

type Props = {
  title: string;
  description: string;
  image?: string;
  imageAlt?: string;
  structuredData?: Record<string, unknown> | Record<string, unknown>[] | null;
  canonicalLink?: string | null;
  video?: VideoMetadata;
  article?: ArticleOpenGraph;
  robots?: string;
  disableCanonical?: boolean;
  /** Set when the page renders `image` through a srcSet, so the preload fetches the same file. */
  imagePreload?: ImagePreload;
  /** The image the page paints first, when it is not the share image; null preloads nothing. */
  preloadImage?: string | null;
};

const Head = (props: Props): JSX.Element => {
  const { asPath } = useRouter();

  const { title, description, image, imageAlt, video, article } = props;
  const imageUrl = image || `${getEnvLink()}${DEFAULT_IMAGE}`;
  // Always use clean canonical URL (no query params) for og:url and canonical
  const canonicalUrl = props.disableCanonical
    ? null
    : props.canonicalLink || getCanonicalUrl(asPath);
  const isVideo = Boolean(video?.url);
  const preloadImage =
    props.preloadImage === undefined ? image : props.preloadImage;
  const robotsContent = props.robots || (!isProduction ? 'noindex' : undefined);

  return (
    <NextHead>
      <title>{title}</title>
      {props.structuredData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(props.structuredData).replace(
              /</g,
              '\\u003c'
            ),
          }}
        ></script>
      )}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {/*
        LCP preload: the LCP element is the poster image (paints before the
        video frames decode), so we preload it with fetchpriority="high" on
        both video and image post pages.
      */}
      {preloadImage && (
        <link
          rel="preload"
          as="image"
          href={preloadImage}
          {...({
            fetchpriority: 'high',
            ...(props.imagePreload && {
              imagesrcset: props.imagePreload.srcSet,
              imagesizes: props.imagePreload.sizes,
            }),
          } as Record<string, string>)}
        />
      )}

      <meta
        httpEquiv="delegate-ch"
        content="sec-ch-width https://cdn-media.millions.co; sec-ch-dpr https://cdn-media.millions.co; sec-ch-viewport-width https://cdn-media.millions.co;"
      />
      <link rel="icon" href="/favicon.png" />
      <link rel="apple-touch-icon" href="/favicon.png" />
      <meta name="theme-color" content="#d8a445" />
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, user-scalable=yes"
      />
      {robotsContent && <meta name="robots" content={robotsContent} />}
      {robotsContent && <meta name="googlebot" content={robotsContent} />}

      <meta
        property="fb:app_id"
        content={process.env.NEXT_PUBLIC_FACEBOOK_APP_ID}
      />
      <meta name="twitter:site" content="@millionsdotco" />
      {isVideo && video ? (
        <>
          <meta property="og:type" content="video.other" />
          <meta property="og:video" content={video.url} />
          <meta property="og:video:url" content={video.url} />
          <meta property="og:video:secure_url" content={video.url} />
          {video.type && <meta property="og:video:type" content={video.type} />}
          {video.width && (
            <meta property="og:video:width" content={String(video.width)} />
          )}
          {video.height && (
            <meta property="og:video:height" content={String(video.height)} />
          )}
          {video.duration && (
            <meta
              property="og:video:duration"
              content={String(video.duration)}
            />
          )}
          <meta property="og:image" content={imageUrl} />
          {imageAlt && <meta property="og:image:alt" content={imageAlt} />}
          <meta property="og:video:image" content={imageUrl} />
        </>
      ) : (
        <meta property="og:type" content={article ? 'article' : 'website'} />
      )}
      {!isVideo && article && (
        <>
          {article.publishedTime && (
            <meta
              property="article:published_time"
              content={article.publishedTime}
            />
          )}
          {article.modifiedTime && (
            <meta
              property="article:modified_time"
              content={article.modifiedTime}
            />
          )}
          {article.author && (
            <meta property="article:author" content={article.author} />
          )}
          {article.tags.map((tag) => (
            <meta
              key={`article:tag:${tag}`}
              property="article:tag"
              content={tag}
            />
          ))}
        </>
      )}
      <meta property="og:locale" content="en_US" />
      <meta property="og:site_name" content="MILLIONS" />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      {!isVideo && <meta property="og:image" content={imageUrl} />}
      {!isVideo && imageAlt && (
        <meta property="og:image:alt" content={imageAlt} />
      )}

      <meta
        name="twitter:card"
        content={isVideo ? 'player' : 'summary_large_image'}
      />
      {canonicalUrl && <meta name="twitter:url" content={canonicalUrl} />}
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      {imageAlt && <meta name="twitter:image:alt" content={imageAlt} />}
    </NextHead>
  );
};

export default Head;
